(() => {
  // Accessories and Components share the existing filter UI, but never download
  // full collection pages or construct hundreds of invisible product cards.
  window.ElitePCS = window.ElitePCS || {};
  window.ElitePCS.createCatalog = (root) => {
    const template = root.querySelector('[data-elite-catalog-card-template]');
    const products = new WeakMap();
    const facets = window.ElitePCS.catalogFacets?.create(root);
    const total = Number(root.dataset.catalogTotal) || 0;
    const indexes = new Map();
    const readyIndexes = new Map();
    const pageRequests = new Map();
    const pageBatchSize = 48;
    const imageScopes = new Map();
    // Keep the first photo visible until the second one has loaded successfully.
    const prepareHoverImage = (media) => {
      const image = media.querySelector('.elite-catalog-hover-image');
      if (!image) return;
      const ready = () => media.classList.toggle('elite-catalog-media--hover-ready', image.naturalWidth > 0);
      image.addEventListener('load', ready);
      image.addEventListener('error', () => media.classList.remove('elite-catalog-media--hover-ready'));
      if (image.complete) ready();
    };
    root.querySelectorAll('.elite-catalog-media--hover').forEach(prepareHoverImage);
    const searchText = (value) => String(value || '').normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const matchesSearch = (card, query) => {
      const terms = searchText(query).split(/\s+/).filter(Boolean);
      return terms.every(term => (card.dataset.catalogSearch || '').includes(term));
    };
    const availabilityKey = (values = []) => {
      const stock = [...new Set(values)].filter(value => ['in-stock', 'out-of-stock'].includes(value));
      return stock.length === 1 ? stock[0] : '';
    };

    const requestPage = (page, stock = '', paged = false, sort = 'featured', priority = 'auto') => {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', paged ? 'elite-catalog-page' : 'elite-catalog-index');
      url.searchParams.set('page', String(page));
      if (stock) url.searchParams.set('filter.v.availability', stock === 'in-stock' ? '1' : '0');
      if (sort === 'price-asc' || sort === 'price-desc') {
        url.searchParams.set('sort_by', sort === 'price-asc' ? 'price-ascending' : 'price-descending');
      }
      const key = url.href;
      if (pageRequests.has(key)) return pageRequests.get(key);
      const request = (async () => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', priority });
          if (!response.ok) throw new Error(`Collection index failed: ${response.status}`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = JSON.parse(doc.querySelector('[data-elite-catalog-index]')?.textContent || 'null');
          if (!data || !Array.isArray(data.products) || !Number.isInteger(data.pages) || !Number.isInteger(data.total)) {
            throw new Error('Collection index is incomplete.');
          }
          return data;
        } finally {
          window.clearTimeout(timeout);
        }
      })().catch(error => { pageRequests.delete(key); throw error; });
      pageRequests.set(key, request);
      return request;
    };

    const toCard = (product, index) => {
      const card = document.createElement('div');
      card.className = 'elite-listing__product';
      card.setAttribute('data-elite-product-card', '');
      card.dataset.filterAccessoryCategory = product.category || product.type || '';
      card.dataset.filterAvailability = product.available ? 'in-stock' : 'out-of-stock';
      const price = Number(product.price);
      card.dataset.filterPriceRange = price < 5000 ? 'under-50' : price < 15000 ? '50-150' : price < 30000 ? '150-300' : '300-plus';
      if (price >= 30000) {
        const detailedRange = price < 50000 ? '300-500' : price < 75000 ? '500-750' : price < 100000 ? '750-1000' : price < 150000 ? '1000-1500' : price < 200000 ? '1500-2000' : '2000-plus';
        card.dataset.filterPriceRange += `|${detailedRange}`;
      }
      card.dataset.sortPrice = String(price);
      card.dataset.sortIndex = String(index);
      card.dataset.catalogSearch = searchText([product.title, product.vendor, product.type, product.category, ...(product.skus || [])].join(' '));
      card.__eliteCatalogFacets = window.ElitePCS.catalogFacets?.extract(product) || {};
      Object.entries(card.__eliteCatalogFacets).forEach(([key, values]) => {
        const dataKey = 'filter' + key.split('_').map(part => part[0].toUpperCase() + part.slice(1)).join('');
        card.dataset[dataKey] = values.join('|');
      });
      products.set(card, product);
      return card;
    };

    const peek = (availability = []) => readyIndexes.get(availabilityKey(availability));
    const load = (availability = [], { priority = 'auto' } = {}) => {
      const stock = availabilityKey(availability);
      if (indexes.has(stock)) return indexes.get(stock);
      if (!total) return Promise.resolve([]);
      // Cache the promise immediately: rapid filter changes share one request.
      const pending = (async () => {
        const first = await requestPage(1, stock, false, 'featured', priority);
        const pages = [first];
        let nextPage = 2;
        await Promise.all(Array.from({ length: Math.min(3, Math.max(0, first.pages - 1)) }, async () => {
          while (nextPage <= first.pages) {
            const page = nextPage++;
            pages[page - 1] = await requestPage(page, stock, false, 'featured', priority);
          }
        }));
        const entries = pages.flatMap((page) => page.products);
        if (entries.length !== first.total || new Set(entries.map((product) => product.id)).size !== entries.length) {
          throw new Error('Collection changed while loading. Please retry.');
        }
        const cards = entries.map(toCard);
        readyIndexes.set(stock, cards);
        return cards;
      })().catch((error) => {
        indexes.delete(stock);
        pageRequests.clear();
        throw error;
      });
      indexes.set(stock, pending);
      return pending;
    };

    // Background preparation shares the foreground request, but never replaces
    // visible products or exposes a loading/error state of its own.
    const preload = (availability = []) => load(availability, { priority: 'low' }).catch(() => null);
    const imageSizes = '(min-width: 1200px) 300px, (min-width: 750px) 33vw, 92vw';
    const imageSrcset = (source) => [240, 360, 480, 600, 700].map((width) => {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return `${url} ${width}w`;
    }).join(', ');
    const warmedImages = new Map();
    const warmImages = (cards) => {
      cards.slice(0, 4).forEach((card) => {
        const source = products.get(card)?.image;
        if (!source || warmedImages.has(source)) return;
        const image = new Image();
        warmedImages.set(source, image);
        image.fetchPriority = 'low';
        image.decoding = 'async';
        image.sizes = imageSizes;
        image.srcset = imageSrcset(source);
        image.src = source;
        image.onerror = () => warmedImages.delete(source);
      });
    };

    const loadPage = async ({ availability = [], sort = 'featured', page = 1, pageSize = 12 } = {}) => {
      if (!total) return { cards: [], total: 0, page: 1 };
      const stock = availabilityKey(availability);
      const start = (page - 1) * pageSize;
      const firstBatch = Math.floor(start / pageBatchSize) + 1;
      const first = await requestPage(firstBatch, stock, true, sort);
      const lastPage = Math.max(1, Math.ceil(first.total / pageSize));
      if (page > lastPage) return loadPage({ availability, sort, page: lastPage, pageSize });
      const lastBatch = Math.min(first.pages, Math.floor((start + pageSize - 1) / pageBatchSize) + 1);
      const batches = [first];
      for (let batch = firstBatch + 1; batch <= lastBatch; batch++) {
        batches.push(await requestPage(batch, stock, true, sort));
      }
      const offset = start % pageBatchSize;
      const entries = batches.flatMap(batch => batch.products).slice(offset, offset + pageSize);
      if (entries.length !== Math.min(pageSize, Math.max(0, first.total - start)) || new Set(entries.map(product => product.id)).size !== entries.length) {
        pageRequests.clear();
        throw new Error('Collection changed while loading. Please retry.');
      }
      return { cards: entries.map((product, index) => toCard(product, start + index)), total: first.total, page };
    };

    // Browse photographed products progressively. Search and category filters
    // still use load() so they can find products outside the loaded batches.
    const loadImagePage = ({ availability = [], sort = 'featured', page = 1, pageSize = 20 } = {}) => {
      const stock = availabilityKey(availability);
      const key = [stock, sort].join(':');
      if (!imageScopes.has(key)) {
        imageScopes.set(key, { entries: [], ids: new Set(), next: 1, pages: null, pending: Promise.resolve() });
      }
      const scope = imageScopes.get(key);
      const request = scope.pending.then(async () => {
        const end = Math.max(1, page) * pageSize;
        while (scope.entries.length <= end && (scope.pages === null || scope.next <= scope.pages)) {
          const batch = await requestPage(scope.next, stock, true, sort);
          const photographed = batch.products.filter(product => product.image);
          if (photographed.some(product => scope.ids.has(product.id))
              || new Set(photographed.map(product => product.id)).size !== photographed.length) {
            imageScopes.delete(key);
            pageRequests.clear();
            throw new Error('Collection changed while loading. Please retry.');
          }
          photographed.forEach(product => scope.ids.add(product.id));
          scope.entries.push(...photographed);
          scope.pages = batch.pages;
          scope.next += 1;
        }
        const complete = scope.next > scope.pages;
        const lastPage = Math.max(1, Math.ceil(scope.entries.length / pageSize));
        const currentPage = complete ? Math.min(Math.max(1, page), lastPage) : Math.max(1, page);
        const start = (currentPage - 1) * pageSize;
        return {
          cards: scope.entries.slice(start, start + pageSize).map((product, index) => toCard(product, start + index)),
          page: currentPage,
          total: complete ? scope.entries.length : null,
          hasNext: scope.entries.length > start + pageSize || !complete,
        };
      });
      // Serialise changes to a scope; concurrent page clicks share cached batches.
      scope.pending = request.catch(() => {});
      return request;
    };

    const plainText = (html) => {
      const text = document.createElement('template');
      text.innerHTML = html;
      return text.content.textContent;
    };
    const formatMoney = (cents) => {
      const format = template.dataset.moneyFormat || '{{amount}}';
      const formats = {
        amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','], amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_space_separator: [2, ' ', ','], amount_no_decimals_with_space_separator: [0, ' ', ','],
        amount_with_period_and_space_separator: [2, ' ', '.'], amount_with_apostrophe_separator: [2, "'", '.']
      };
      return plainText(format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
        const [precision, thousands, decimal] = formats[token] || formats.amount;
        const parts = (cents / 100).toFixed(precision).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      }));
    };

    const render = (source, index = 0) => {
      const product = products.get(source);
      if (!product) {
        const card = source.cloneNode(true);
        card.querySelectorAll('.elite-catalog-media--hover').forEach(prepareHoverImage);
        return card;
      }
      const card = source.cloneNode(false);
      card.append(template.content.cloneNode(true));
      const title = card.querySelector('[data-catalog-title]');
      title.textContent = product.title;
      title.title = product.title;
      card.querySelectorAll('[data-catalog-link]').forEach((link) => { link.href = product.url; });
      card.querySelector('.elite-product-card__media-image-link').setAttribute('aria-label', `View ${product.title}`);
      card.querySelector('[data-catalog-price]').textContent = plainText(product.priceLabel);
      const strike = card.querySelector('[data-catalog-strike]');
      const strikeAmount = template.dataset.strikeMode === 'fixed'
        ? product.price + Math.round(Number(template.dataset.strikeFixed) * 100)
        : Math.floor(product.price * (100 + Number(template.dataset.strikePercentage)) / 100);
      if (template.dataset.strikeEnabled === 'true' && product.price > 0 && strikeAmount > product.price) {
        strike.textContent = formatMoney(strikeAmount);
        strike.setAttribute('aria-label', `Previous price ${strike.textContent}`);
        if (template.dataset.strikePlacement === 'above-current') strike.parentElement.prepend(strike);
        if (template.dataset.strikePlacement === 'under-title') card.querySelector('.elite-product-card__details-link').append(strike);
      } else {
        strike.remove();
      }
      const badge = card.querySelector('[data-catalog-stock]');
      badge.textContent = product.available ? 'In stock' : 'Out of stock';
      badge.classList.add('badge--inventory', product.available ? 'badge--inventory-in-stock' : 'badge--inventory-out-of-stock');
      const media = card.querySelector('[data-catalog-image]');
      if (product.image) {
        const img = document.createElement('img');
        img.alt = product.imageAlt || product.title;
        img.width = 700;
        img.height = 520;
        img.loading = index < 4 ? 'eager' : 'lazy';
        img.fetchPriority = index < 4 ? 'high' : 'auto';
        img.decoding = 'async';
        img.sizes = imageSizes;
        img.srcset = imageSrcset(product.image);
        img.src = product.image;
        media.append(img);
        if (product.secondImage && product.secondImage !== product.image) {
          const second = img.cloneNode(false);
          second.className = 'elite-catalog-hover-image';
          second.alt = '';
          second.loading = 'lazy';
          second.fetchPriority = 'low';
          second.srcset = imageSrcset(product.secondImage);
          second.src = product.secondImage;
          media.classList.add('elite-catalog-media--hover');
          media.append(second);
          prepareHoverImage(media);
        }
      } else {
        media.textContent = 'Image coming soon';
      }
      card.querySelector('[data-catalog-variant]').value = product.variant;
      if (!product.available || !product.variant) {
        const button = card.querySelector('[data-catalog-add]');
        button.disabled = true;
        button.type = 'button';
        button.classList.add('elite-product-card__cart-button--disabled');
        button.querySelector('svg')?.remove();
        button.querySelector('span').textContent = 'Sold out';
      }
      return card;
    };
    const hasImage = (card) => Boolean(products.get(card)?.image);
    return { load, peek, preload, warmImages, loadPage, loadImagePage, render, matchesSearch, hasImage, syncFacets: facets?.sync, restoreFacets: facets?.restore };
  };
})();
